import type { Source } from 'clients/CampaignsClient/CandidateResponse';
import type { CandidateDto } from 'clients/NotificationsClient/NotificationsClient.types';
import type { IndeedConversation } from 'shared/legacy/hooks/useBrowserExtension/indeed/indeed.types';
import type {
  AdditionalRequestType,
  CrawlerErrorResponse,
  Credential,
  GetLinkedinRecruiterCandidateHandler,
  GetWerknlProfilesHandler,
  RefreshProfileHandler,
  UpdateCredentialByIdParams,
  WerknlProfiles,
} from './types';

import { BaseClient } from 'clients/BaseClient/BaseClient';
import { hosts } from 'config';
import { isAxiosError } from 'helpers/clientHelpers';
import { replaceUrlToLocal } from 'helpers/replaceUrlToLocal';
import { CandidateNotFoundException, NoCreditsAvailableForMonsterboard } from 'shared/legacy/exceptions';

export class CrawlerClient extends BaseClient {
  constructor() {
    super();
    this.downloadProfile = this.downloadProfile.bind(this);
  }

  async downloadProfile(fileId: string, candidateName: string) {
    const [extension] = fileId.split('.').reverse();
    const fileName = `${candidateName}.${extension}`;

    await this.fetchFile(fileName, {
      url: `${hosts.crawlerWebApi()}/files/cv/download/${fileId}`,
    });
  }

  refreshProfile: RefreshProfileHandler = async ({ es_person_id: id, platform, force, requestDetail = false }) => {
    let hostUrl: string | undefined;

    if (['cvt', 'linkedin', 'indeed'].includes(platform)) {
      hostUrl = hosts.crawlerSocketApi();
    } else {
      hostUrl = hosts.crawlerWebApi();
    }

    let detailsResult: AdditionalRequestType | undefined;

    try {
      if (['monsterboard_v2'].includes(platform) && requestDetail) {
        const platformParameter = platform === 'monsterboard_v2' ? 'monsterboard' : platform;

        await this.request<AdditionalRequestType>({
          url: `${hostUrl}/${platformParameter}/requestDetail/${id}`,
          method: 'GET',
        });
      }

      const { data: refreshRequest } = await this.request<{ candidate: Source } | undefined>({
        url: `${hostUrl}/candidate/${platform}/${id}/refresh`,
        method: 'GET',
        params: {
          ...(force === true && { force }),
        },
      });

      if (Object.keys(refreshRequest ?? {}).length === 0 && Object.keys(detailsResult ?? {}).length === 0) {
        return undefined;
      }

      return {
        ...(refreshRequest ? refreshRequest?.candidate : {}),
      } as Source;
    } catch (e) {
      if (isAxiosError<CrawlerErrorResponse>(e)) {
        if (e.response?.status === 404) {
          throw new CandidateNotFoundException(`Failed to refresh candidate ${id} from ${platform}`);
        }
        if (e.response?.status === 402 && platform === 'monsterboard_v2') {
          throw new NoCreditsAvailableForMonsterboard();
        }
      }

      throw e;
    }
  };

  isWerknlActive = async () => {
    return this.request({
      url: `${hosts.crawlerSocketApi()}/werknl/message/is_active`,
    });
  };

  async getCredentials(platform: string) {
    return this.request<Credential[]>({
      url: replaceUrlToLocal(`${hosts.crawlerWebApi()}/jwt/${platform}/credentials`, { debug: true }),
      method: 'GET',
    });
  }

  async updateCredentialById({ platform, id, data }: UpdateCredentialByIdParams) {
    return this.request<void>({
      url: replaceUrlToLocal(`${hosts.crawlerWebApi()}/jwt/${platform}/credentials/${id}`, { debug: true }),
      method: 'PUT',
      data,
    });
  }

  getWerknlProfiles: GetWerknlProfilesHandler = async () => {
    return this.request<WerknlProfiles[]>({
      url: `${hosts.crawlerWebApi()}/werknl/profiles`,
      method: 'GET',
    });
  };

  getLinkedinRecruiterCandidate: GetLinkedinRecruiterCandidateHandler = async (linkedin_candidate_id: string) => {
    return this.request({
      url: `${hosts.crawlerWebApi()}/linkedin_recruiter_pro/recruiter/candidate/${linkedin_candidate_id}`,
      method: 'GET',
    });
  };

  getLinkedinCandidate: GetLinkedinRecruiterCandidateHandler = async (linkedin_candidate_id: string) => {
    return this.request({
      url: `${hosts.crawlerWebApi()}/linkedin_recruiter_pro/candidate/${linkedin_candidate_id}`,
      method: 'GET',
    });
  };

  getIndeedCandidateMessages = async ({
    tenantId,
    candidateName,
    esPersonId,
  }: {
    tenantId: number | string;
    candidateName: string;
    esPersonId: string;
  }) => {
    return this.request({
      url: `${hosts.crawlerWebApi()}/indeed/messages/${candidateName}/${esPersonId}/${tenantId}`,
      method: 'GET',
    });
  };

  saveNewIndeedMessage = async ({
    messages,
    credentialEmail,
    candidate,
  }: {
    messages: IndeedConversation[];
    credentialEmail: string | null;
    candidate: CandidateDto;
  }) => {
    if (!credentialEmail) return;

    return this.request({
      url: `${hosts.crawlerWebApi()}/indeed/save_new_message/${candidate.candidate_name}/${candidate.source_id}`,
      method: 'POST',
      data: {
        credentialUsername: credentialEmail,
        messages,
      },
    });
  };
}

export const crawlerClient = new CrawlerClient();
